import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { trackCta, getLeadSource, clearLeadSource, fbTrack, newEventId } from "@/lib/track";

const API = `/api`;

const clubs = [
  { id: "maia", label: "Clube da Maia" },
  { id: "barcelos", label: "Clube de Barcelos" },
];

export const LeadForm = ({ preselectedClub }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    club: preselectedClub || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (preselectedClub) {
      setFormData(prev => ({ ...prev, club: preselectedClub }));
    }
  }, [preselectedClub]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.club) {
      toast.error("Por favor preenche todos os campos.");
      return;
    }

    if (!consent) {
      toast.error("Por favor aceita ser contactado pelo Justfit para continuar.");
      return;
    }

    setIsSubmitting(true);

    try {
      const eventId = newEventId();
      await axios.post(`${API}/leads`, {
        first_name: formData.name,
        last_name: "",
        email: formData.email,
        phone: formData.phone,
        club: formData.club,
        source: getLeadSource() || "formulario_homepage",
        campaign_id: window.__JF_ACTIVE_CAMPAIGN?.id || "",
        event_id: eventId,
        event_source_url: window.location.href,
        interests: [],
        message: "",
      });
      clearLeadSource();
      fbTrack("Lead", eventId);
      if (window.gtag) window.gtag("event", "generate_lead", { club: formData.club, campaign_id: window.__JF_ACTIVE_CAMPAIGN?.id || "" });
      navigate(`/obrigado-${formData.club}`, { state: { leadId: eventId, email: formData.email, phone: formData.phone } });
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Erro ao enviar. Por favor tenta novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-20 lg:py-28 bg-[#1A1A1A]" data-testid="lead-form-section">
      <div id="deixarmensagem"></div>
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="text-[#FED601] text-sm font-bold uppercase tracking-widest">
            Estamos Aqui Para Ti
          </span>
          <h2 className="text-4xl lg:text-5xl font-black text-white mt-2 mb-4">
            Junta-te a Nós
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Preenche o formulário e entraremos em contacto nas próximas 24 horas. 
            A tua transformação começa aqui!
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <form onSubmit={handleSubmit} className="bg-[#252525] border border-white/10 rounded-xl p-8 lg:p-10" data-testid="lead-form">
              {/* Name + Phone */}
              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <Label htmlFor="name" className="text-white mb-2 block font-medium">
                    Nome <span className="text-[#FED601]">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    onFocus={() => { if (window.gtag && !window.__jfFormStart) { window.__jfFormStart = 1; window.gtag("event", "form_start", { club: formData.club || "", campaign_id: window.__JF_ACTIVE_CAMPAIGN?.id || "" }); } }}
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="O teu nome"
                    className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 h-12 rounded-lg focus:border-[#FED601]"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-white mb-2 block font-medium">
                    Telemóvel <span className="text-[#FED601]">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="912 345 678"
                    className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 h-12 rounded-lg focus:border-[#FED601]"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Email + Club */}
              <div className="grid md:grid-cols-2 gap-5 mb-6">
                <div>
                  <Label htmlFor="email" className="text-white mb-2 block font-medium">
                    Email <span className="text-[#FED601]">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="o.teu@email.com"
                    className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 h-12 rounded-lg focus:border-[#FED601]"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block font-medium">
                    Clube Pretendido <span className="text-[#FED601]">*</span>
                  </Label>
                  <Select
                    value={formData.club}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, club: value }))}
                  >
                    <SelectTrigger 
                      aria-label="Clube pretendido"
                      className="bg-[#1A1A1A] border-white/10 text-white h-12 rounded-lg focus:border-[#FED601]"
                      data-testid="select-club"
                    >
                      <SelectValue placeholder="Seleciona um clube" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#252525] border-white/10">
                      {clubs.map((club) => (
                        <SelectItem 
                          key={club.id} 
                          value={club.id}
                          className="text-white hover:bg-[#FED601] hover:text-black focus:bg-[#FED601] focus:text-black"
                        >
                          {club.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Consentimento */}
              <label className="flex items-start gap-3 mb-5 cursor-pointer" data-testid="consent-label">
                <Checkbox
                  checked={consent}
                  onCheckedChange={(checked) => setConsent(checked === true)}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#FED601] data-[state=checked]:border-[#FED601] data-[state=checked]:text-black"
                  data-testid="consent-checkbox"
                />
                <span className="text-gray-300 text-sm leading-relaxed">Aceito ser contactado pelo Justfit para receber informações sobre os serviços e campanhas.</span>
              </label>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                data-testid="submit-lead-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-black border-t-transparent rounded-full" />
                    A Enviar...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Enviar Pedido
                  </>
                )}
              </button>
              <p className="text-gray-500 text-xs leading-relaxed mt-4 text-center" data-testid="privacy-note">
                Ao enviar os teus dados, autorizas o Justfit a contactar-te sobre os seus serviços. Consulta a nossa{" "}
                <a href="https://www.justfitcenter.pt/privacy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#FED601]">Política de Privacidade</a>.
              </p>
            </form>
        </motion.div>
      </div>
    </section>
  );
};
